/* Wall-clock launch time in an explicit IANA time zone, independent of visitor location. */
(function () {
  'use strict';

  function parseLaunchTime(value, timeZone) {
    const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/.exec((value || '').trim());
    if (!match) return NaN;
    const [year, month, day, hour, minute, second] = match.slice(1).map((part) => Number(part || 0));
    if (year < 2000 || month < 1 || month > 12 || day < 1 || hour > 23 || minute > 59 || second > 59) return NaN;
    const wallTime = Date.UTC(year, month - 1, day, hour, minute, second);
    const date = new Date(wallTime);
    if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return NaN;
    try {
      const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: (timeZone || '').trim(), year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
      });
      const localStamp = (timestamp) => {
        const parts = Object.fromEntries(formatter.formatToParts(new Date(timestamp)).map((part) => [part.type, part.value]));
        return Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute), Number(parts.second));
      };
      // Gather offsets on both sides of a possible DST transition.
      const offsets = new Set([-36, 0, 36].map((hours) => {
        const sample = wallTime + hours * 3600000;
        return localStamp(sample) - sample;
      }));
      const candidates = [...offsets].map((offset) => wallTime - offset).filter((stamp) => localStamp(stamp) === wallTime);
      // Nonexistent spring times and ambiguous fall times need an explicit correction.
      return candidates.length === 1 ? candidates[0] : NaN;
    } catch (_) {
      return NaN;
    }
  }

  function remainingParts(target, now) {
    const total = Math.max(0, Math.ceil((target - now) / 1000));
    return [Math.floor(total / 86400), Math.floor(total / 3600) % 24, Math.floor(total / 60) % 60, total % 60];
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { parseLaunchTime, remainingParts };
  if (typeof window === 'undefined' || !window.customElements || window.customElements.get('product-launch-countdown')) return;

  class ProductLaunchCountdown extends HTMLElement {
    connectedCallback() {
      this.stop();
      this.clock = this.querySelector('[data-launch-clock]');
      this.notice = this.querySelector('[data-launch-notice]');
      this.values = this.querySelectorAll('[data-launch-value]');
      this.target = parseLaunchTime(this.dataset.launchAt, this.dataset.timeZone);
      this.onVisibility = () => this.tick();
      document.addEventListener('visibilitychange', this.onVisibility);
      this.tick();
      if (this.dataset.matches === 'true' && Number.isFinite(this.target) && this.target > Date.now()) {
        this.interval = window.setInterval(() => this.tick(), 1000);
      }
    }

    tick() {
      let message = '';
      if (this.dataset.hasProducts !== 'true') message = 'Select the products that should display this countdown.';
      else if (this.dataset.matches !== 'true') message = 'This product is not selected for this countdown.';
      else if (!Number.isFinite(this.target)) message = 'Set a valid launch time (YYYY-MM-DD HH:mm) and IANA time zone. Avoid skipped or repeated daylight-saving hours.';
      else if (this.target <= Date.now()) message = 'The launch time has passed. This block is hidden on the storefront.';

      const visible = !message;
      this.clock.hidden = !visible;
      this.notice.hidden = visible || this.dataset.designMode !== 'true';
      this.notice.textContent = message;
      this.hidden = !visible && this.dataset.designMode !== 'true';
      if (visible) {
        remainingParts(this.target, Date.now()).forEach((value, index) => {
          this.values[index].textContent = String(value).padStart(2, '0');
        });
      } else {
        window.clearInterval(this.interval);
      }
    }

    stop() {
      window.clearInterval(this.interval);
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      this.stop();
    }
  }

  window.customElements.define('product-launch-countdown', ProductLaunchCountdown);
})();
